import {
  Button,
  BUTTON_STATUS,
  BUTTON_TYPE,
  COLORS,
  COMMON_STYLES,
  CrossIcon,
  PlusMinusButtonWithInput,
  SIZE,
  Spinner,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from '@dehaat/dds'
import React, { useContext, useEffect, useState } from 'react'
import {
  View,
  Text,
  StyleSheet,
  Modal,
  Pressable,
  ScrollView,
} from 'react-native'

import ToastAndroid from 'app/mocks/ToastAndroid'

import { ResponsiveContext } from 'app/context/ResponsiveUIContext'
import SearchInput from 'app/components/reusable/SearchInput'
import RoundImageWithName from 'app/components/reusable/RoundImageWithName'
import { FarmerLivestock, Livestock } from '../../models/farmer'
import fetchAllLivestocks from '../../apis/fetchAllLivestocks'
import updateFarmerLivestocks from '../../apis/updateFarmerLivestocks'
import RoundImageWithIcon from 'app/components/reusable/RoundImageWithIcon'
import { DIMENSION_HEIGHT, isMobileDevice } from 'app/utils/common'

interface CropSelectionWithFlagProps {
  farmerId: string
  livestocks: FarmerLivestock[]
  onClose: () => void
  onSubmit: (livestocks: string) => void
}

const LivestocksSelectionModal = ({
  farmerId,
  livestocks,
  onClose,
  onSubmit,
}: CropSelectionWithFlagProps) => {
  const { mediaStyles } = useContext(ResponsiveContext)
  const [loading, setLoading] = useState(true)
  const [allLivestocks, setAllLivestocks] = useState<Livestock[]>([])
  const [searchQuery, setSearchQuery] = useState('')

  const isDesktop = !isMobileDevice()

  const [selectedLivestock, setSelectedLivestock] = useState<
    Record<string, Livestock>
  >(() => {
    return livestocks.reduce((data: Record<string, Livestock>, livestock) => {
      data[livestock.livestock_id] = {
        id: livestock.livestock_id,
        image_url: livestock.image_url,
        name: livestock.name,
        quantity: livestock.quantity,
      }
      return data
    }, {})
  })

  const searchResults = allLivestocks.filter(livestock =>
    livestock.name.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  const isLivestocksModified =
    livestocks
      .map(ls => ls.id)
      .sort()
      .join() === Object.keys(selectedLivestock).sort().join()

  const fetchAndUpdateAllLivestocks = async () => {
    const response = await fetchAllLivestocks()
    if (response) {
      setAllLivestocks(response)
    }
  }

  const handleLivestockQty = (id: string, qty: number) => {
    const data = { ...selectedLivestock }
    data[id].quantity = qty
    setSelectedLivestock(data)
  }

  const handleLivestockClick = (livestock: Livestock) => {
    const livestocks = { ...selectedLivestock }
    if (selectedLivestock[livestock.id]) {
      delete livestocks[livestock.id]
    } else {
      livestocks[livestock.id] = { ...livestock, quantity: 1 }
    }
    setSelectedLivestock(livestocks)
  }

  const handleSubmitLivestocks = async () => {
    setLoading(true)
    const success = await updateFarmerLivestocks(
      farmerId,
      Object.values(selectedLivestock),
    )
    if (success) {
      ToastAndroid.show('Livestocks Upated Successfully!', ToastAndroid.SHORT)
      onSubmit(
        Object.values(selectedLivestock)
          .map(ls => ls.name)
          .join(),
      )
    } else {
      ToastAndroid.show('Something went wrong. Please try ', ToastAndroid.SHORT)
    }
  }

  useEffect(() => {
    fetchAndUpdateAllLivestocks().finally(() => setLoading(false))
  }, [])

  return (
    <Modal>
      <View
        style={[
          COMMON_STYLES.fullHeight,
          mediaStyles({ desktopStyle: COMMON_STYLES.row }),
        ]}
      >
        <View
          style={[
            COMMON_STYLES.pv2,
            mediaStyles({
              desktopStyle: [styles.selectedCropContainer, styles['ph5/2']],
              mobileStyle: COMMON_STYLES.ph1,
            }),
          ]}
        >
          <View style={[COMMON_STYLES.row, COMMON_STYLES.justifyBetween]}>
            <Text
              style={[
                TYPOGRAPHY.HEADING1,
                VARIANTS_STYLES.EMPHASIZED,
                TEXT_STYLES.textNeutral90,
              ]}
            >
              Livestock owned by farmer
            </Text>
            {!isDesktop && (
              <Pressable onPress={onClose}>
                <CrossIcon height={32} width={32} />
              </Pressable>
            )}
          </View>
          <ScrollView
            showsVerticalScrollIndicator={false}
            showsHorizontalScrollIndicator={false}
            horizontal={isMobileDevice()}
          >
            <View style={[COMMON_STYLES.row, COMMON_STYLES.flexWrap]}>
              {Object.values(selectedLivestock).map(ls => (
                <RoundImageWithIcon
                  crop={ls}
                  key={ls.id}
                  containerStyle={[styles.margin, COMMON_STYLES.mv1]}
                  activeImagecontainerStyle={styles.selectedImageContainer}
                  isIcon
                  imageStyle={styles.image}
                  iconContainerStyle={styles.crossStyle}
                  icon={
                    <CrossIcon height={16} width={16} strokeColor={'white'} />
                  }
                  onClick={() => handleLivestockClick(ls)}
                  onIconClick={() => handleLivestockClick(ls)}
                  isImageSelected={true}
                  label={ls.quantity}
                />
              ))}
            </View>
          </ScrollView>
        </View>

        <View
          style={[
            mediaStyles({
              desktopStyle: styles.divider,
              mobileStyle: styles.dividerHz,
            }),
          ]}
        />
        <View
          style={[
            COMMON_STYLES.pt2,
            COMMON_STYLES.flex1,
            mediaStyles({
              desktopStyle: [styles.selectedCropContainer, styles['ph5/2']],
              mobileStyle: COMMON_STYLES.ph1,
            }),
          ]}
        >
          {loading && <Spinner type="on-screen" />}
          <View
            style={[
              COMMON_STYLES.row,
              COMMON_STYLES.fullWidth,
              COMMON_STYLES.justifyBetween,
            ]}
          >
            <Text
              style={[
                TYPOGRAPHY.HEADING1,
                VARIANTS_STYLES.EMPHASIZED,
                TEXT_STYLES.textNeutral90,
              ]}
            >
              Choose Animals
            </Text>
            {isDesktop && (
              <Pressable onPress={onClose}>
                <CrossIcon height={32} width={32} />
              </Pressable>
            )}
          </View>
          <SearchInput
            placeholder={'Search animals'}
            onChange={(text: string) => setSearchQuery(text)}
            value={searchQuery}
            inputContainerStyle={COMMON_STYLES.mt1}
          />

          {!searchQuery ? (
            <>
              <ScrollView style={[COMMON_STYLES['mt5/4'], COMMON_STYLES.flex1]}>
                <View style={[COMMON_STYLES.row, COMMON_STYLES.flexWrap]}>
                  {allLivestocks.map((ls, index) => (
                    <Pressable
                      key={ls.id}
                      style={{
                        width: 166,
                        height: 211,
                        marginRight: 24,
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        paddingTop: 24,
                        paddingBottom: 8,
                        backgroundColor: COLORS.neutral10,
                        borderRadius: 16,
                        borderWidth: 1,
                        borderColor: COLORS.neutral20,
                        marginBottom: 24,
                        paddingHorizontal: 8,
                      }}
                    >
                      <RoundImageWithIcon
                        crop={ls}
                        key={ls.id}
                        containerStyle={[styles.margin, COMMON_STYLES.mv1]}
                        activeImagecontainerStyle={
                          styles.selectedImageContainer
                        }
                        isIcon
                        imageStyle={styles.image}
                        iconContainerStyle={styles.crossStyle}
                        icon={
                          <CrossIcon
                            height={16}
                            width={16}
                            strokeColor={'white'}
                          />
                        }
                        onClick={() => handleLivestockClick(ls)}
                        onIconClick={() => handleLivestockClick(ls)}
                        isImageSelected={!!selectedLivestock[ls.id]}
                      />
                      {!!selectedLivestock[ls.id] && (
                        <PlusMinusButtonWithInput
                          onDeleteClick={() => handleLivestockClick(ls)}
                          onMinusClick={() =>
                            handleLivestockQty(
                              ls.id,
                              selectedLivestock[ls.id].quantity - 1,
                            )
                          }
                          onPlusClick={() =>
                            handleLivestockQty(
                              ls.id,
                              selectedLivestock[ls.id].quantity + 1,
                            )
                          }
                          quantity={selectedLivestock[ls.id].quantity}
                        />
                      )}
                    </Pressable>
                  ))}
                </View>
              </ScrollView>
              <View
                style={[COMMON_STYLES.row, COMMON_STYLES['pv1/2'], styles.gap1]}
              >
                <Button
                  onClick={onClose}
                  text={'Cancel'}
                  type={BUTTON_TYPE.SECONDARY}
                  size={SIZE.large}
                  buttonStyle={COMMON_STYLES.flex1}
                />
                <Button
                  onClick={() => {
                    if (!isLivestocksModified) {
                      handleSubmitLivestocks()
                    }
                  }}
                  text={'Save'}
                  size={SIZE.large}
                  buttonStyle={COMMON_STYLES.flex1}
                  status={
                    isLivestocksModified
                      ? BUTTON_STATUS.DISABLED
                      : BUTTON_STATUS.ACTIVE
                  }
                />
              </View>
            </>
          ) : (
            <>
              {searchResults.length ? (
                <>
                  <Text
                    style={[
                      COMMON_STYLES.mv1,
                      TYPOGRAPHY.HEADING3,
                      VARIANTS_STYLES.MEDIUM,
                      TEXT_STYLES.textNeutral70,
                    ]}
                  >
                    Search Results
                  </Text>
                  <View style={[COMMON_STYLES.row, COMMON_STYLES.flexWrap]}>
                    {searchResults.map(crop => (
                      <RoundImageWithName
                        key={crop.id}
                        name={crop.name}
                        imgUrl={crop.image_url}
                        handleItemClick={() => {
                          handleLivestockClick(crop)
                          setSearchQuery('')
                        }}
                        style={COMMON_STYLES['mv3/4']}
                      />
                    ))}
                  </View>
                </>
              ) : (
                <Text
                  style={[
                    COMMON_STYLES.mv1,
                    TYPOGRAPHY.HEADING3,
                    VARIANTS_STYLES.MEDIUM,
                    TEXT_STYLES.textNeutral70,
                  ]}
                >
                  No Animal Found
                </Text>
              )}
            </>
          )}
        </View>
      </View>
    </Modal>
  )
}

const styles = StyleSheet.create({
  divider: {
    borderRightWidth: 1,
    borderColor: COLORS.neutral20,
    height: DIMENSION_HEIGHT,
  },
  dividerHz: {
    borderBottomWidth: 1,
    borderColor: COLORS.neutral20,
  },
  'ph5/2': {
    paddingHorizontal: 40,
  },

  selectedCropContainer: {
    width: 400,
  },

  margin: {
    marginHorizontal: 10,
    marginBottom: 4,
    width: 80,
  },

  image: {
    height: 80,
    width: 80,
    borderWidth: 1,
    borderColor: COLORS.neutral20,
    borderRadius: 9999,
  },

  selectedImageContainer: {
    borderColor: COLORS.primary100,
    borderWidth: 1,
  },

  crossStyle: {
    position: 'absolute',
    top: 0,
    right: -3,
    zIndex: 1,
    padding: 5,
    borderRadius: 9999,
    backgroundColor: COLORS.primary100,
    borderWidth: 2,
    borderColor: COLORS.white,
  },

  gap1: {
    gap: 16,
  },
})

export default LivestocksSelectionModal
